import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import PersonPage from './pages/PersonPage';
import PeoplePage from './pages/PeoplePage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import CompaniesPage from './pages/CompaniesPage';
import CompanyDetailPage from './pages/CompanyDetailPage';
import SpinoffsPage from './pages/SpinoffsPage';
import InsightsPage from './pages/InsightsPage';
import AlumniPage from './pages/AlumniPage';
import AdminPage from './pages/AdminPage';
import AccessDeniedPage from './pages/AccessDeniedPage';
import PrivacyPage from './pages/privacy/PrivacyPage';
import AboutPage from './pages/about/AboutPage';
import AppSidebar from './components/AppSidebar';
import StaticDataWarning from './components/StaticDataWarning';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AuthProvider } from './auth/AuthContext';
import AuthGate from './auth/AuthGate';
import RoleRoute from './auth/RoleRoute';
import { COMPANY_ROLES, ALUMNI_ROLES, ADMIN_ROLES } from './auth/roles';

function AppLayout() {
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="flex items-center gap-2 px-3 py-2 md:hidden border-b border-ctu-border bg-white">
          <SidebarTrigger />
        </div>
        <StaticDataWarning />
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  );
}

// Layout route that gates everything beneath it behind authentication.
function AuthGatedLayout() {
  return (
    <AuthGate>
      <Outlet />
    </AuthGate>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public: reachable without a session so the no-role deny redirect
              from the backend callback doesn't loop back through AuthGate. */}
          <Route path="/access-denied" element={<AccessDeniedPage />} />

          {/* Public: the GDPR/privacy notice must be reachable without a session
              so it is "publicly available" to researchers (GDPR Art. 14). */}
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* Public: informal "about" page, reachable without a session so it
              mirrors the privacy notice. */}
          <Route path="/about" element={<AboutPage />} />

          <Route element={<AuthGatedLayout />}>
            <Route element={<AppLayout />}>
              <Route path="/" element={<Navigate to="/people" replace />} />
              <Route path="/insights" element={<InsightsPage />} />
              <Route path="/dashboard" element={<Navigate to="/insights?view=charts" replace />} />
              <Route path="/network" element={<Navigate to="/insights?view=network" replace />} />
              <Route path="/people" element={<PeoplePage />} />
              <Route path="/person" element={<Navigate to="/people" replace />} />
              <Route path="/person/:id" element={<PersonPage />} />
              <Route path="/outcomes" element={<ProjectsPage />} />
              <Route path="/outcome/:id" element={<ProjectDetailPage />} />
              <Route
                path="/companies"
                element={<RoleRoute required={COMPANY_ROLES}><CompaniesPage /></RoleRoute>}
              />
              <Route
                path="/company/:id"
                element={<RoleRoute required={COMPANY_ROLES}><CompanyDetailPage /></RoleRoute>}
              />
              <Route
                path="/alumni"
                element={<RoleRoute required={ALUMNI_ROLES}><AlumniPage /></RoleRoute>}
              />
              <Route path="/ctu-companies" element={<SpinoffsPage />} />
              <Route
                path="/admin"
                element={<RoleRoute required={ADMIN_ROLES}><AdminPage /></RoleRoute>}
              />
            </Route>
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
